import React, { useState } from 'react';
import { Menu, Bell, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import GlobalSearch from './GlobalSearch';

const Header = ({ setSidebarOpen, toggleSidebar, sidebarOpen }) => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="pfe-app-header sticky top-0 z-40 flex h-16 items-center justify-between border-b border-gray-200/80 bg-white/95 px-3 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur-md print:hidden sm:h-18 sm:px-4 md:px-6">
      {/* Left Side */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Mobile Menu Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2.5 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all"
        >
          <Menu className="w-6 h-6" />
        </motion.button>
        
        {/* Desktop Toggle Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleSidebar}
          className="hidden lg:block p-2.5 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all"
          title={sidebarOpen ? "Réduire la sidebar" : "Ouvrir la sidebar"}
        >
          <Menu className="w-6 h-6" />
        </motion.button>
        
        {/* Removed duplicate welcome message */}
      </div>

      {/* Center - Global Search (Desktop only) */}
      <div className="hidden sm:block flex-1 max-w-2xl mx-2 sm:mx-4 min-w-0">
        <GlobalSearch />
      </div>

      {/* Right Side */}
      <div className="flex items-center space-x-3">
        {/* Notifications */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/notifications')}
          className="relative p-2.5 rounded-2xl hover:bg-gradient-to-br hover:from-red-50 hover:to-red-100/50 transition-all duration-300 hover:shadow-md group"
        >
          <Bell className="w-6 h-6 text-gray-700 group-hover:text-red-600 transition-colors" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-full shadow-lg ring-2 ring-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </motion.button>

        {/* Profile Menu */}
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center space-x-3 p-2 pr-4 rounded-2xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-red-50/30 transition-all border border-transparent hover:border-red-100 hover:shadow-md"
          >
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/30 ring-2 ring-white print:hidden">
              <span className="text-white font-bold text-base">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="hidden sm:block text-left print:hidden">
              <p className="text-sm font-bold text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize font-medium">{user?.role}</p>
            </div>
          </motion.button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {showProfileMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ type: 'spring', duration: 0.2 }}
                className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 py-2 z-50 overflow-hidden"
              >
                <motion.button
                  whileHover={{ x: 4, backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
                  onClick={() => {
                    navigate(user?.role === 'admin' ? '/admin-profile' : '/profile');
                    setShowProfileMenu(false);
                  }}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm font-semibold text-gray-700 transition-all"
                >
                  <User className="w-5 h-5 text-gray-500" />
                  <span>Mon Profil</span>
                </motion.button>
                <hr className="my-1 border-gray-100" />
                <motion.button
                  whileHover={{ x: 4, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                  onClick={handleLogout}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm font-bold text-red-600 transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Déconnexion</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;

